import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Observe an element's own width (ResizeObserver), for layouts that must
 * branch on the space they ACTUALLY have rather than on the viewport.
 *
 * Viewport media queries (`min-[820px]:`) lie inside the app shell: the
 * sidebar eats ~256px, so a 900px-wide window leaves a two-pane layout far
 * less room than the breakpoint implies — and the sidebar can be collapsed at
 * runtime, which no media query can see. Returns `null` until the first
 * measurement so callers can avoid a first-paint flash.
 *
 * Usage:
 *   const [ref, width] = useContainerWidth<HTMLDivElement>();
 *   const twoPane = width == null || width >= 820;
 */
export function useContainerWidth<T extends HTMLElement>(): [
  (node: T | null) => void,
  number | null,
] {
  return useMeasuredWidth<T, number>(identity);
}

/**
 * The same observer, but the owner only stores whether the element is
 * narrower than `px`. A resize that does not cross the line sets the same
 * boolean, which React bails out of, so the owner does not re-render on every
 * resize tick. `null` until the first measurement.
 *
 * Usage:
 *   const [ref, narrow] = useContainerNarrowerThan<HTMLDivElement>(760);
 */
export function useContainerNarrowerThan<T extends HTMLElement>(px: number): [
  (node: T | null) => void,
  boolean | null,
] {
  const map = useCallback((width: number) => width < px, [px]);
  return useMeasuredWidth<T, boolean>(map);
}

function identity(width: number): number {
  return width;
}

function useMeasuredWidth<T extends HTMLElement, V>(map: (width: number) => V): [
  (node: T | null) => void,
  V | null,
] {
  const [element, setElement] = useState<T | null>(null);
  const [value, setValue] = useState<V | null>(null);
  const mapRef = useRef(map);
  useEffect(() => {
    mapRef.current = map;
  });

  // Callback ref so the observer re-attaches if the node is swapped out.
  const ref = useCallback((node: T | null) => setElement(node), []);

  useEffect(() => {
    if (!element) return;
    if (typeof ResizeObserver === "undefined") {
      // jsdom / very old browsers: fall back to a one-shot measurement so the
      // layout still resolves to a sane branch.
      setValue(mapRef.current(element.getBoundingClientRect().width));
      return;
    }
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setValue(mapRef.current(entry.contentRect.width));
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);

  return [ref, value];
}
